/**
 * The single seam between the Ask UI and whatever eventually answers questions.
 *
 * One interface, one place to swap. Everything above it — the runner, the panel, the
 * message list — is written against `AskProvider` and knows nothing about which model,
 * transport, or vendor is behind it.
 *
 * There is still no model SDK or API key anywhere in this app, and there must never be
 * one — see `httpAskProvider` (./httpProvider.ts), the shipped implementation. It talks
 * ONLY to our own API (`POST /ask/admin/answer/stream`) and relays whatever the server
 * streams back; any model credentials live server-side, in
 * apps/api/src/features/ask/ask.service.ts. If the server has nothing configured, IT
 * answers with an `error` event, and the UI renders that honestly. Nothing in this app
 * generates prose on its own. A fabricated answer in a help surface is worse than an
 * obviously missing one, because a confident invention is indistinguishable from a real
 * answer at a glance — and the whole point of this surface is that people trust it.
 */
import { httpAskProvider } from './httpProvider'
import type { AskMessage, AskStreamEvent } from './types'

/** A guide passage handed to the provider as grounding for a docs-shaped answer. */
export interface AskDocContext {
  slug: string
  category: string
  title: string
  headingId?: string
  headingText?: string
  excerpt: string
}

export interface AskRequest {
  question: string
  /** Prior turns in this conversation, oldest first. */
  history: AskMessage[]
  /**
   * Retrieved docs passages. Retrieval runs for every question, regardless of what it
   * turns out to be about, so this MAY be empty — a data question ("where are my
   * certificates scanned most") legitimately matches no docs, and an empty result must
   * not stop the turn from reaching the provider. The provider decides what to do with an
   * empty context (fall back to tools, or answer `{ type: 'no_docs' }` / `{ type:
   * 'no_data', missing }` — VRG-227's split, see ./types.ts); it is not the runner's job
   * to pre-judge that from the doc match alone.
   */
  context: AskDocContext[]
  /** Aborted when the user presses stop, or when the panel unmounts mid-turn. */
  signal: AbortSignal
}

export interface AskProvider {
  /** Identifies the implementation in logs and in the UI's "not configured" state. */
  readonly id: string
  /** False means the UI should not offer to send. */
  readonly configured: boolean
  /**
   * Produce one assistant turn as a stream of events.
   *
   * An async iterable rather than a callback so the runner can `for await` and let
   * cancellation fall out of breaking the loop, instead of tracking subscriptions.
   */
  stream(request: AskRequest): AsyncIterable<AskStreamEvent>
}

/**
 * The answering contract every provider on this seam must honor, regardless of what
 * question turns out to be about. There is no mode switch to route on — the model itself
 * decides whether a question needs `request.context` (retrieved docs passages), a tool
 * call, or both, the same way it decides anything else about how to answer:
 *
 *  - It answers `request.context` faithfully when it grounds the reply — no inventing
 *    something that reads as documentation. When neither the context nor a tool covers
 *    the question, it emits `{ type: 'no_docs' }` (a how-to question no guide covers) or
 *    `{ type: 'no_data', missing }` (a legitimate data question Vera has no field for —
 *    VRG-227 split these apart so the right next step is offered for each) and stops;
 *    the UI turns that into a LINK to /support/new that the user chooses to follow. It
 *    must never file the request itself — silently opening a ticket because a question
 *    was hard is a surprising side effect, and it trains people not to ask.
 *  - It gets EXACTLY THREE TOOLS: query_scans, query_certificates, query_owners,
 *    hand-written and read-only, fetched from GET /ask/admin/tools. No text-to-SQL,
 *    ever. The company scope is applied server-side from the session and is not an
 *    argument the model can set — see apps/api/src/features/ask/ask.tools.ts. Emit
 *    `tool_call` / `tool_result` events as they happen so a number in an answer can be
 *    traced to the query that produced it.
 *
 * This contract is enforced server-side (apps/api/src/features/ask/ask.service.ts);
 * `httpAskProvider` is a pure relay and cannot check any of it itself — see its own file
 * comment. `notConfiguredAskProvider` below is not bound by default anymore, but still
 * has to satisfy the same contract's first rule (never fabricate), which is why it stays
 * exported: the honest fallback this surface always had, kept in reserve.
 */
export const notConfiguredAskProvider: AskProvider = {
  id: 'not-configured',
  configured: false,
  // eslint-disable-next-line require-yield
  async *stream(): AsyncIterable<AskStreamEvent> {
    throw new Error(
      'Ask Vera has no answer provider configured yet, so questions cannot be answered. ' +
        'The question you typed has been kept.',
    )
  },
}

/**
 * The provider the app uses.
 *
 * A module-level constant rather than a React context: there is exactly one provider for
 * the whole app, chosen at build time, and threading a context through for a value that
 * never varies per subtree buys nothing.
 */
export const askProvider: AskProvider = httpAskProvider
